import { Claim, Payment, Participant, Session } from './types';

/** A refusal from the split API. Branch on [code], not the sentence. */
export class SplitApiError extends Error {
  constructor(
    message: string,
    readonly status: number | null,
    readonly code: string | null = null,
  ) {
    super(message);
    this.name = 'SplitApiError';
  }

  /** The browser couldn't reach the server. */
  get offline() {
    return this.status === null;
  }
}

export type Joined = {
  participant: Participant;
  participantToken: string;
  session: Session;
  /** The number was already on the split: the same person, continuing here. */
  reconnected: boolean;
};

type Fetch = typeof fetch;

/**
 * The guest routes of `/api/split-sessions/public/:token`. The guest's own
 * token goes in the `x-participant-token` header.
 */
export function splitApi(baseUrl = process.env.REACT_APP_API_BASE_URL ?? '', fetchImpl: Fetch = fetch) {
  const root = `${baseUrl.replace(/\/$/, '')}/api/split-sessions/public`;

  async function call<T>(
    method: string,
    path: string,
    options: { body?: unknown; participantToken?: string } = {},
  ): Promise<{ data: T; status: number }> {
    let response: Response;
    try {
      response = await fetchImpl(`${root}${path}`, {
        method,
        headers: {
          ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
          ...(options.participantToken ? { 'x-participant-token': options.participantToken } : {}),
        },
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
      });
    } catch {
      throw new SplitApiError('You’re offline. Check your connection and try again.', null);
    }
    let json: any = null;
    try {
      json = await response.json();
    } catch {
      /* not JSON; handled below */
    }
    if (!response.ok || !json || json.success === false) {
      const message =
        response.status === 429
          ? 'Too many tries. Wait a minute, then try again.'
          : typeof json?.error === 'string' && json.error.trim()
          ? json.error
          : 'Something went wrong. Try again.';
      throw new SplitApiError(message, response.status, typeof json?.code === 'string' ? json.code : null);
    }
    return { data: json.data as T, status: response.status };
  }

  const at = (shareToken: string) => `/${encodeURIComponent(shareToken)}`;
  const person = (shareToken: string, participantId: string) =>
    `${at(shareToken)}/participants/${encodeURIComponent(participantId)}`;

  return {
    async session(shareToken: string): Promise<Session> {
      return (await call<Session>('GET', at(shareToken))).data;
    },

    async join(shareToken: string, details: { displayName: string; phoneNumber: string }): Promise<Joined> {
      const { data, status } = await call<Omit<Joined, 'reconnected'>>('POST', `${at(shareToken)}/participants`, {
        body: details,
      });
      return { ...data, reconnected: status === 200 };
    },

    async setClaims(
      shareToken: string,
      participantId: string,
      participantToken: string,
      claims: Claim[],
    ): Promise<Session & { sharedItemIds?: string[] }> {
      return (
        await call<Session & { sharedItemIds?: string[] }>('PUT', `${person(shareToken, participantId)}/claims`, {
          body: { claims },
          participantToken,
        })
      ).data;
    },

    /** Records the payment as pending before the dialer opens; tapping again reuses it. */
    async pay(
      shareToken: string,
      participantId: string,
      participantToken: string,
      forParticipantId?: string,
    ): Promise<{ payment: Payment; session: Session }> {
      return (
        await call<{ payment: Payment; session: Session }>('POST', `${person(shareToken, participantId)}/payments`, {
          body: { method: 'momo', ...(forParticipantId ? { forParticipantId } : {}) },
          participantToken,
        })
      ).data;
    },

    /** "I've paid". */
    async reported(shareToken: string, participantId: string, participantToken: string, paymentId: string): Promise<Session> {
      return (
        await call<Session>('POST', `${person(shareToken, participantId)}/payments/${encodeURIComponent(paymentId)}/reported`, {
          body: {},
          participantToken,
        })
      ).data;
    },

    /** "Pay Bob back": a pending payback, and Bob's number for the dialer. */
    async payback(
      shareToken: string,
      participantId: string,
      participantToken: string,
      toParticipantId: string,
    ): Promise<{ payment: Payment; toPhoneNumber: string | null; session: Session }> {
      return (
        await call<{ payment: Payment; toPhoneNumber: string | null; session: Session }>(
          'POST',
          `${person(shareToken, participantId)}/paybacks`,
          { body: { toParticipantId }, participantToken },
        )
      ).data;
    },

    /** "Got it": the person paid back says it arrived. */
    async paybackReceived(
      shareToken: string,
      participantId: string,
      participantToken: string,
      paymentId: string,
    ): Promise<Session> {
      return (
        await call<Session>('POST', `${person(shareToken, participantId)}/paybacks/${encodeURIComponent(paymentId)}/received`, {
          body: {},
          participantToken,
        })
      ).data;
    },

    /** "Settle another way": the person owed lets the debt go. */
    async settleDebt(
      shareToken: string,
      participantId: string,
      participantToken: string,
      fromParticipantId: string,
    ): Promise<Session> {
      return (
        await call<Session>('POST', `${person(shareToken, participantId)}/debts/${encodeURIComponent(fromParticipantId)}/settled`, {
          body: {},
          participantToken,
        })
      ).data;
    },
  };
}

export type SplitApi = ReturnType<typeof splitApi>;
